import { Layout } from "@/components/Layout/Layout";
import { Plus, Pencil, Eye, ChefHat, UserX, UserCheck, Download } from "lucide-react";
import { useEffect, useState } from "react";
import { MainHeading } from "@/components/common/MainHeading";
import { callApi, ThrowError } from "@/api/callApi";
import { API_ENDPOINT, API_URL } from "@/api/constant";
import { IStore, IStoreListResponse } from "@/interfaces/Store/store.interface";
import CommonTable, { Action, TableColumn } from "@/components/common/CommonTable";
import { IFilter, IPageInfo } from "@/interfaces/common.interface";
import { StatusBadge } from "@/components/common/StatusBadge";
import { formatTime12Hour, GetFilterUrl, getCookies, getRole, showToastNotification, downloadExcel } from "@/utility/helper";
import { useFilter } from "@/hooks/useFilter";
import Pagination from "@/components/common/Pagination";
import Button from "@/components/common/Button";
import DateRangePicker from "@/components/common/DateRange";
import { Range } from "react-date-range";
import { format } from "date-fns";
import { useNavigate } from "react-router-dom";
import { useAppSelector, RootState } from "@/redux/store";
import { getRolePrefix, RoutesLink } from "@/utility/RouteLinks";
import { Switch } from "@/components/ui/switch";
import { TableFilters } from "@/components/common/TableFilters";
import { usePermissions } from "@/hooks/usePermissions";
import { RESOURCES } from "@/context/permissions";
import { useRoleNavigate } from "@/hooks/useRoleNavigate";
import ConfirmationModal from "@/components/common/ConfirmationModal";


export const Stores = () => {
    const navigate = useRoleNavigate();
    const backNavigate = useNavigate();

    const { canCreate, canEdit, canView, canExport } = usePermissions();
    const [stores, setStores] = useState<IStore[]>([]);
    const [loading, setLoading] = useState(false);

    const [blockTarget, setBlockTarget] = useState<IStore | null>(null);
    const [blockLoading, setBlockLoading] = useState(false);

    const { filters, debouncedFilters, updateFilter, handlePageChange, handlePageSizeChange } = useFilter<IFilter>({
        page: 1,
        limit: 10,
        startDate: '',
        endDate: '',
    });

    const [loadingExport, setLoadingExport] = useState<boolean>(false);

    const handleExport = async () => {
        const queryParams = GetFilterUrl({
            status: filters.status,
            startDate: filters.startDate,
            endDate: filters.endDate,
            sort: filters.sort
        });
        const url = `${API_URL + API_ENDPOINT.stores}/export/excel?${queryParams}`;
        setLoadingExport(true);
        try {
            await downloadExcel(url, 'stores_export.xlsx');
        } catch (error) {
            ThrowError(error);
        } finally {
            setLoadingExport(false);
        }
    };

    const [dateRange, setDateRange] = useState<Range[]>([
        {
            startDate: undefined,
            endDate: undefined,
            key: 'selection',
        },
    ]);

    const handleDateChange = (newRange: any) => {
        const resolvedRange = typeof newRange === 'function' ? newRange(dateRange) : newRange;
        setDateRange(resolvedRange);

        const start = resolvedRange[0]?.startDate;
        const end = resolvedRange[0]?.endDate;
        const formattedStart = start ? format(start, 'yyyy-MM-dd') : '';
        const formattedEnd = end ? format(end, 'yyyy-MM-dd') : '';

        if (formattedStart !== filters.startDate || formattedEnd !== filters.endDate) {
            updateFilter({ startDate: formattedStart, endDate: formattedEnd, page: 1 });
        }
    };
    const [pageInfo, setPageInfo] = useState<IPageInfo>({
        limit: 10,
        page: 1,
        total: 0,
        totalPages: 0,
    });

    const fetchStores = async () => {
        setLoading(true);
        try {
            const queryParams = GetFilterUrl(debouncedFilters)
            const response = await callApi<IStoreListResponse>(
                `${API_URL + API_ENDPOINT.stores}/all?${queryParams}`,
                { method: 'GET' }
            );
            setStores(response?.data?.stores || []);
            setPageInfo(
                response?.data?.pageInfo || { limit: 10, page: 1, total: 0, totalPages: 0 },
            );
        } catch (error) {
            ThrowError(error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchStores();
    }, [debouncedFilters.page, debouncedFilters.limit, debouncedFilters.search, debouncedFilters.startDate, debouncedFilters.endDate]);

    const handleBlockToggle = async () => {
        if (!blockTarget) return;
        setBlockLoading(true);
        const newBlockedState = !blockTarget.user?.isBlocked;
        try {
            await callApi(
                `${API_URL}${API_ENDPOINT.user}/block/${blockTarget.userId}?isBlocked=${newBlockedState}`,
                { method: 'PATCH' }
            );
            showToastNotification(
                'success',
                `Store owner has been ${newBlockedState ? 'blocked' : 'unblocked'} successfully.`
            );
            setStores(prev =>
                prev.map(s => s.id === blockTarget.id ? { ...s, user: { ...s.user!, isBlocked: newBlockedState } } : s)
            );
        } catch (error) {
            ThrowError(error);
        } finally {
            setBlockLoading(false);
            setBlockTarget(null);
        }
    };




    const columns: TableColumn<IStore>[] = [
        {
            key: "name",
            header: "Store Name",
            render: (item) => <div className="font-medium text-red-700 flex items-center gap-2">
                <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-red-100/70">
                    {item.media?.find(m => m.referenceType === 'store_logo')?.url ? (
                        <img src={`${API_URL}/${item.media.find(m => m.referenceType === 'store_logo')!.url}`} alt={item.name} className="w-full h-full object-cover" />
                    ) : (
                        <ChefHat size={16} className="text-red-700" />
                    )}
                </span>
                {item.name}
            </div>
        },
        {
            key: "contact",
            header: "Contact",
            render: (item) => <div className="text-muted-foreground">{item.contact}</div>
        },
        {
            key: "address",
            header: "Address",
            render: (item) => (
                <div className="max-w-[200px] truncate text-muted-foreground" title={item.address}>
                    {item.address}
                </div>
            )
        },
        {
            key: "hours",
            header: "Operating Hours",
            render: (item) => (
                <div className="text-muted-foreground text-xs whitespace-nowrap">
                    {formatTime12Hour(item.settings?.openingTime)} - {formatTime12Hour(item.settings?.closingTime)}
                </div>
            )

        },
        {
            key: "status",
            header: "Status",
            render: (item) => (
                <div className="flex flex-col gap-1">
                    <StatusBadge status={item.status} />

                </div>
            )
        },
        {
            key: "blockAction",
            header: "Block Access",
            render: (item) => (
                <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                    <Switch
                        checked={item.user?.isBlocked || false}
                        onCheckedChange={() => {
                            if (canEdit(RESOURCES.STORE)) {
                                setBlockTarget(item);
                            }
                        }}
                        disabled={!canEdit(RESOURCES.STORE)}
                    />
                </div>
            )
        }
    ];

    const actions: Action<IStore>[] = [
        ...(canView(RESOURCES.STORE) ? [{
            text: 'View Details',
            icon: <Eye size={16} strokeWidth={1.5} className="cursor-pointer" />,
            onClick: (record: IStore) => {
                navigate(RoutesLink.storeDetails.replace(':id', record.id));
            },
        }] : []),
        ...(canEdit(RESOURCES.STORE) ? [{
            text: 'Edit',
            icon: <Pencil size={16} strokeWidth={1.5} className="cursor-pointer" />,
            onClick: (record: IStore) => {
                navigate(RoutesLink.EditStore.replace(':id', record.id));
            },
        }] : []),
    ];

    const onlineCount = stores.filter(storeData => storeData.status?.toLowerCase() === 'online').length;

    return (
        <Layout>
            <MainHeading
                title="Stores"
                description={`${stores.length} stores · ${onlineCount} online`}
                actionLabel={canCreate(RESOURCES.STORE) && "Add Store"}
                actionIcon={canCreate(RESOURCES.STORE) && <Plus className="w-4 h-4" />}
                onAction={() => navigate(RoutesLink.AddStore)}
            />
            <div className="mt-6 flex flex-col gap-4">
                <TableFilters
                    searchTerm={filters.search}
                    onSearchChange={(val) => updateFilter({ search: val })}
                    placeholder="Search stores by name..."
                >
                    <div className="flex items-center gap-3 flex-wrap">
                        <DateRangePicker
                            dateRange={dateRange}
                            setDateRange={handleDateChange as any}
                        />
                        {canExport(RESOURCES.STORE) && <Button
                            variant="outline"
                            onClick={() => handleExport()}
                            className="h-10 px-4 flex items-center gap-2 rounded-xl"
                            leftIcon={<Download className="w-4 h-4" />}
                            isLoading={loadingExport}
                        >
                            Export
                        </Button>}
                    </div>
                </TableFilters>
                <CommonTable
                    columns={columns}
                    data={stores}
                    loading={loading}
                    emptyMessage="No stores found. Try adding one!"
                    actions={actions}
                />
                <Pagination
                    currentPage={filters.page || 1}
                    totalPages={pageInfo.totalPages}
                    totalItems={pageInfo.total}
                    itemsPerPage={pageInfo.limit}
                    onPageChange={handlePageChange}
                    onPageSizeChange={handlePageSizeChange}
                />
            </div>

            {/* Block / Unblock Confirmation Modal */}
            <ConfirmationModal
                open={!!blockTarget}
                onOpenChange={(open) => !open && setBlockTarget(null)}
                onConfirm={handleBlockToggle}
                loading={blockLoading}
                type={blockTarget?.user?.isBlocked ? "success" : "danger"}
                title={blockTarget?.user?.isBlocked ? "Unblock Store Owner?" : "Block Store Owner?"}
                message={
                    blockTarget?.user?.isBlocked
                        ? `Are you sure you want to unblock the owner of ${blockTarget?.name}? They will regain full access to their store dashboard.`
                        : `Are you sure you want to block the owner of ${blockTarget?.name}? They will lose access to the store dashboard immediately.`
                }
                confirmText={blockTarget?.user?.isBlocked ? "Yes, Unblock" : "Yes, Block"}
                cancelText="Cancel"
                size="lg"
            />
        </Layout>
    );
};

